import { getCancelWithoutSavingModalEvent } from '@ui-common/components/Modal/CancelWithoutSavingModal'
import { UseWizardFormReturn } from '@ui-common/hooks/useWizardForm'
import { GlobalServicesContext } from '@ui-common/state'
import { FormStep as CommonFormStep } from '@ui-common/steps/FormStep'
import { isDirtyFromUserInteraction } from '@ui-common/utils/formUtils'
import { LogoIconType } from '@ui-components/global/ComponentProps'
import { BackActionTopBar } from '@ui-components/navigation/BackActionTopBar'
import { PropsWithChildren, ReactNode, use } from 'react'
import { FieldValues, useFormState } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { LoanOfferAnalyticsContext } from '../analytics/analyticsContext'
import { FormContext } from './formContext'

export interface StepProps<TFormData extends FieldValues> {
  subHeader: string
  leadText?: string
  formMethods: UseWizardFormReturn<TFormData>
  onSubmit: () => void
  isLoading?: boolean
  allowBack?: boolean
  submitLabel?: string
  customFooter?: ReactNode
  showIntroSectionDivider?: boolean
  disableDirtyCheck?: boolean
}

export const FormStep = <TFormData extends FieldValues>({
  formMethods,
  subHeader,
  onSubmit,
  customFooter,
  allowBack = true,
  children,
  submitLabel,
  showIntroSectionDivider,
  leadText,
  disableDirtyCheck = false,
}: PropsWithChildren<StepProps<TFormData>>) => {
  const intl = useIntl()
  const { step, goBack, exitWithConfirmDialog, goBackToStatus } = use(FormContext)
  const formState = useFormState({ control: formMethods.control })
  const isFormDirty = isDirtyFromUserInteraction(formState)

  const { analyticsService } = use(LoanOfferAnalyticsContext)
  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const showDiscardChangesModal = () => {
    const continueAction = intl.formatMessage({ id: 'common-continue-anyway' })

    modalSend(
      getCancelWithoutSavingModalEvent({
        onConfirm: () => {
          goBackToStatus()
        },
        intl,
        header: intl.formatMessage({ id: 'common-back-to-application-unsaved-header' }),
        text: intl.formatMessage(
          { id: 'common-back-to-application-unsaved-warning' },
          { continue_action: continueAction },
        ),
        primaryActionText: continueAction,
      }),
    )
  }

  const onBackClick = () => {
    if (isFormDirty && !disableDirtyCheck) {
      showDiscardChangesModal()
    } else {
      goBackToStatus()
    }
  }

  return (
    <>
      <BackActionTopBar
        backLabel={intl.formatMessage({ id: 'sec-loans-back-to-application-summary' })}
        onBackClick={onBackClick}
      />
      <CommonFormStep
        customFooter={customFooter}
        formMethods={formMethods}
        icon={LogoIconType.SPankki}
        isEditing={false}
        isLoading={false}
        onBack={allowBack ? goBack : undefined}
        onExit={exitWithConfirmDialog}
        onSubmit={onSubmit}
        shouldShowMainHeader={false}
        stepId={step}
        submitLabelOverride={submitLabel}
        title={''}
        analytics={analyticsService}
        pageNameForAnalytics={step}
        pageIntro={{ title: subHeader, showIntroSectionDivider, leadText }}
        hasDataDependencies={false}
        progressBarOptions={null}
        customBackAction={disableDirtyCheck ? () => goBack() : undefined}
      >
        {children}
      </CommonFormStep>
    </>
  )
}
